import { FC, MouseEvent, useState } from "react";

import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  BarChartOutlined,
  ChevronRight,
  Delete,
  Edit,
  MoreVert,
  Output,
  Share,
} from "@mui/icons-material";

import { HelpBox } from "@/components/atoms";
import { Audience, AudienceActions } from "@/types";

interface AudienceCardMenuProps {
  audience: Audience;
  actions: AudienceActions;
}

export const AudienceCardMenu: FC<AudienceCardMenuProps> = ({
  audience,
  actions
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [audienceSubmenuEl, setAudienceSubmenuEl] =
    useState<null | HTMLElement>(null);
  const [actionSubmenuEl, setActionSubmenuEl] = useState<null | HTMLElement>(
    null,
  );

  const showAudienceMenu = true; // TODO Why wont this be visible?
  const showActionMenu = true; // TODO Why wont this be visible?

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setAudienceSubmenuEl(null);
    setActionSubmenuEl(null);
  };

  const handleOpenAudienceSubmenu = (event: MouseEvent<HTMLElement>) =>
    setAudienceSubmenuEl(event.currentTarget);

  const handleCloseAudienceSubmenu = () => setAudienceSubmenuEl(null);

  const handleOpenActionSubmenu = (event: MouseEvent<HTMLElement>) =>
    setActionSubmenuEl(event.currentTarget);

  const handleDeleteAudience = () => {
    actions.deleteAudience(audience);
    handleCloseMenu();
  };
  const handleEditAudience = () => {
    actions.editAudience(audience);
    handleCloseMenu();
  };

  const handleShareAudience = () => {
    actions.shareAudience(audience);
    handleCloseMenu();
  };

  const handleViewInsights = () => {
    actions.viewInsights(audience);
    handleCloseMenu();
  };

  const handleViewExport = () => {
    actions.exportAudience(audience);
    handleCloseMenu();
  };

  return (
    <>
      {/* Main Menu Button */}
      <HelpBox title="Audience Actions">
        <IconButton
          onClick={handleOpenMenu}
          sx={{ p: 0.5, borderRadius: "6px" }}
        >
          <MoreVert />
        </IconButton>
      </HelpBox>

      {/* Main Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: { style: { width: 140 } },
        }}
      >
        {/* Audience Submenu */}
        {showAudienceMenu && (
          <>
            <MenuItem
              onClick={handleOpenAudienceSubmenu}
              disabled={audience.shared}
            >
              <ListItemText primary="Audience" />
              <ChevronRight />
            </MenuItem>

            {/* Audience Options - Edit, Delete & Share */}
            <Menu
              anchorEl={audienceSubmenuEl}
              open={Boolean(audienceSubmenuEl)}
              onClose={handleCloseAudienceSubmenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItem onClick={handleEditAudience}>
                <ListItemIcon>
                  <Edit color="primary" />
                </ListItemIcon>
                <ListItemText primary="Rename" />
              </MenuItem>
              <MenuItem onClick={handleDeleteAudience}>
                <ListItemIcon>
                  <Delete color="primary" />
                </ListItemIcon>
                <ListItemText primary="Delete" />
              </MenuItem>
              <MenuItem onClick={handleShareAudience}>
                <ListItemIcon>
                  <Share color="primary" />
                </ListItemIcon>
                <ListItemText primary="Share" />
              </MenuItem>
            </Menu>
            <Divider />
          </>
        )}

        {/* Actions Submenu */}
        {showActionMenu && (
          <>
            <MenuItem
              onClick={handleOpenActionSubmenu}
              disabled={!audience.audience_id}
            >
              <ListItemText primary="Actions" />
              <ChevronRight />
            </MenuItem>

            {/* Actions - Insights & Export */}
            <Menu
              anchorEl={actionSubmenuEl}
              open={Boolean(actionSubmenuEl)}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItem onClick={handleViewInsights}>
                <ListItemIcon>
                  <BarChartOutlined color="primary" />
                </ListItemIcon>
                <ListItemText primary="Insights" />
              </MenuItem>
              <MenuItem onClick={handleViewExport}>
                <ListItemIcon>
                  <Output color="primary" />
                </ListItemIcon>
                <ListItemText primary="Export" />
              </MenuItem>
            </Menu>
          </>
        )}
      </Menu>
    </>
  );
};
